import type { Paragraph, PhrasingContent, Root, RootContent, Text } from "mdast";
import type { Plugin } from "unified";
import { visit } from "unist-util-visit";

// Remark plugin that converts `[[name]]` tokens in markdown text nodes into
// a custom `artifact-ref` element. The element is mapped to React via the
// `components` prop on ReactMarkdown, where it resolves+renders the artifact.
//
// The `name` is opaque on the wire — the backend resolves it to an artifact
// id via `GET /conversations/{id}/artifacts/previews?names=...`. We never
// parse or validate it here; whatever appears between the brackets is the key.
//
// A paragraph whose entire content is artifact-refs is replaced with one
// block-level ref per child, so block renderers (dataframe <table>, image
// dialog, etc.) aren't placed inside <p>.

const ARTIFACT_REF_RE = /\[\[([^\]]+)\]\]/g;
const ARTIFACT_REF_HNAME = "artifact-ref";

interface ArtifactRefData {
  hName: typeof ARTIFACT_REF_HNAME;
  hProperties: { name: string };
}

function isArtifactRef(node: PhrasingContent): node is PhrasingContent & { data: ArtifactRefData } {
  return (node.data as { hName?: string } | undefined)?.hName === ARTIFACT_REF_HNAME;
}

function isWhitespaceText(node: PhrasingContent): boolean {
  // Artifact-refs are text nodes with value="" plus hName data; check the
  // hName first so they aren't mistaken for blank whitespace.
  if (isArtifactRef(node)) return false;
  return node.type === "text" && node.value.trim() === "";
}

function artifactRefBlock(name: string): RootContent {
  // mdast-util-to-hast honors `data.hName` regardless of node type, so the
  // rendered element is <artifact-ref> rather than <p>. We use `paragraph`
  // because it's a valid block-content type in every container we care
  // about (root, blockquote, listItem).
  return {
    type: "paragraph",
    children: [],
    data: { hName: ARTIFACT_REF_HNAME, hProperties: { name } },
  } as unknown as RootContent;
}

export const remarkArtifactRefs: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, "text", (node: Text, index, parent) => {
      if (index == null || !parent) return;

      const value = node.value;
      ARTIFACT_REF_RE.lastIndex = 0;
      if (!ARTIFACT_REF_RE.test(value)) return;
      ARTIFACT_REF_RE.lastIndex = 0;

      const children: PhrasingContent[] = [];
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = ARTIFACT_REF_RE.exec(value)) !== null) {
        if (match.index > lastIndex) {
          children.push({ type: "text", value: value.slice(lastIndex, match.index) });
        }
        children.push({
          type: "text",
          value: "",
          data: {
            hName: ARTIFACT_REF_HNAME,
            hProperties: { name: match[1] },
          },
        } as unknown as PhrasingContent);
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < value.length) {
        children.push({ type: "text", value: value.slice(lastIndex) });
      }

      parent.children.splice(index, 1, ...children);
    });

    visit(tree, "paragraph", (paragraph: Paragraph, index, parent) => {
      if (index == null || !parent) return;
      const significant = paragraph.children.filter((c) => !isWhitespaceText(c));
      if (significant.length === 0 || !significant.every(isArtifactRef)) {
        return;
      }
      const blocks = significant.map((ref) => artifactRefBlock(ref.data.hProperties.name));
      (parent.children as RootContent[]).splice(index, 1, ...blocks);
      return index + blocks.length;
    });
  };
};
